import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useCutoffDay, showDate } from '../utils/months';
import { downloadBlob } from '../services/api';

const styles = {
  h1: { fontSize: 22, fontWeight: 600, marginBottom: 20 },
  tabs: { display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #eee' },
  tab: (active) => ({
    padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer', border: 'none',
    backgroundColor: 'transparent', borderBottom: active ? '2px solid #4a9eff' : '2px solid transparent',
    color: active ? '#4a9eff' : '#888', marginBottom: -2,
  }),
  actionBar: { display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' },
  btn: { padding: '8px 16px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGreen: { padding: '8px 16px', backgroundColor: '#27ae60', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnAmber: { padding: '8px 16px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { padding: '8px 16px', backgroundColor: '#ccc', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'default' },
  selectedCount: { fontSize: 13, color: '#666' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  checkbox: { width: 16, height: 16, cursor: 'pointer' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500,
    backgroundColor: color === 'green' ? '#e6f9e6' : color === 'amber' ? '#fff8e6' : color === 'red' ? '#fee' : '#f0f0f0',
    color: color === 'green' ? '#27ae60' : color === 'amber' ? '#f39c12' : color === 'red' ? '#e74c3c' : '#666',
  }),
  link: { color: '#4a9eff', textDecoration: 'none' },
  message: { padding: '16px 20px', backgroundColor: '#e6f9e6', borderRadius: 8, color: '#27ae60', fontSize: 14, marginBottom: 16 },
};

export default function Renewals() {
  const cutoff = useCutoffDay(); // dates as months when sold by the month
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [log, setLog] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setLoading(true);
    if (tab === 'pending') {
      api.getPendingRenewals().then(setPending).finally(() => setLoading(false));
    } else {
      api.getRenewalLog().then(setLog).finally(() => setLoading(false));
    }
  }, [tab]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === pending.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pending.map(p => p.id)));
    }
  };

  const handleSend = async (method) => {
    if (selected.size === 0) return;
    setSending(true);
    setMessage('');
    try {
      const ids = Array.from(selected);
      const result = await api.sendRenewals(ids, method);

      if (result.blob) {
        // Letter PDF returned
        downloadBlob(result.blob, 'renewal-letters.pdf');
        setMessage('Renewal letters PDF downloaded');
      } else {
        const parts = [];
        if (result.emails_sent) parts.push(`${result.emails_sent} email${result.emails_sent !== 1 ? 's' : ''} sent`);
        if (result.emails_failed) parts.push(`${result.emails_failed} failed`);
        if (result.letters_generated) parts.push(`${result.letters_generated} letter${result.letters_generated !== 1 ? 's' : ''} generated`);
        setMessage(parts.join(', ') || 'Done');
      }

      setSelected(new Set());
      api.getPendingRenewals().then(setPending);
    } catch (err) {
      setMessage('Error: ' + (err.message || 'Failed to send'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 style={styles.h1}>Renewals</h1>

      <div style={styles.tabs}>
        <button style={styles.tab(tab === 'pending')} onClick={() => setTab('pending')}>Pending ({pending.length})</button>
        <button style={styles.tab(tab === 'log')} onClick={() => setTab('log')}>History</button>
      </div>

      {message && <div style={styles.message}>{message}</div>}

      {tab === 'pending' && (
        <>
          <div style={styles.actionBar}>
            <span style={styles.selectedCount}>{selected.size} selected</span>
            <button
              style={selected.size > 0 && !sending ? styles.btnGreen : styles.btnDisabled}
              onClick={() => handleSend('email')}
              disabled={selected.size === 0 || sending}
            >Send Email</button>
            <button
              style={selected.size > 0 && !sending ? styles.btnAmber : styles.btnDisabled}
              onClick={() => handleSend('letter')}
              disabled={selected.size === 0 || sending}
            >Generate Letters</button>
            <button
              style={selected.size > 0 && !sending ? styles.btn : styles.btnDisabled}
              onClick={() => handleSend('both')}
              disabled={selected.size === 0 || sending}
            >Send Both</button>
          </div>

          {loading ? <div>Loading...</div> : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <input type="checkbox" style={styles.checkbox}
                      checked={pending.length > 0 && selected.size === pending.length}
                      onChange={toggleAll} />
                  </th>
                  <th style={styles.th}>Subscriber</th>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Expires</th>
                  <th style={styles.th}>Days Left</th>
                  <th style={styles.th}>Reminder Stage</th>
                  <th style={styles.th}>Sent</th>
                  <th style={styles.th}>Pref. Method</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <tr key={p.id} style={{ backgroundColor: selected.has(p.id) ? '#f0f7ff' : 'transparent' }}>
                    <td style={styles.td}>
                      <input type="checkbox" style={styles.checkbox}
                        checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)} />
                    </td>
                    <td style={styles.td}>
                      <Link to={`/subscribers/${p.subscriber_id}`} style={styles.link}>{p.subscriber_name}</Link>
                    </td>
                    <td style={styles.td}>{p.title_name}</td>
                    <td style={styles.td}>{showDate(p.end_date, cutoff)}</td>
                    <td style={styles.td}>
                      <span style={styles.badge(p.days_remaining <= 7 ? 'red' : p.days_remaining <= 14 ? 'amber' : 'green')}>
                        {p.days_remaining}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 13 }}>
                        {p.current_reminder_days >= 7 ? `${Math.round(p.current_reminder_days / 7)}-week` : `${p.current_reminder_days}-day`}
                      </span>
                    </td>
                    <td style={styles.td}>{p.reminders_sent}/{p.total_reminders}</td>
                    <td style={styles.td}>{p.preferred_renewal_method}</td>
                  </tr>
                ))}
                {pending.length === 0 && (
                  <tr><td style={styles.td} colSpan={8}>No renewals due at this time</td></tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'log' && (
        loading ? <div>Loading...</div> : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Subscriber</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Method</th>
                <th style={styles.th}>Reminder</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {log.map(l => (
                <tr key={l.id}>
                  <td style={styles.td}>{l.subscriber_name}</td>
                  <td style={styles.td}>{l.title_name}</td>
                  <td style={styles.td}>{l.method}</td>
                  <td style={styles.td}>{l.reminder_days ? `${l.reminder_days}d` : '-'}</td>
                  <td style={styles.td}>
                    <span style={styles.badge(l.status === 'sent' ? 'green' : l.status === 'failed' ? 'red' : 'amber')}>
                      {l.status}
                    </span>
                  </td>
                  <td style={styles.td}>{new Date(l.sent_at).toLocaleDateString('en-GB')}</td>
                  <td style={styles.td}>{l.notes || '-'}</td>
                </tr>
              ))}
              {log.length === 0 && (
                <tr><td style={styles.td} colSpan={7}>No renewal history yet</td></tr>
              )}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
